/* ===========================================================================
   filterAndValidateProperties.ts
   ---------------------------------------------------------------------------
   – Generates Yup schemas (generateYupValidationSchema supports jsonSchema)
   – Validates & assigns defaults
   – Enforces per-property write access controls (via accessContext)
   – Emits plain Fastify debug logs (no wrappers, no fanciness) 😌
   ========================================================================== */

import * as Yup from 'yup';
import type { FastifyInstance } from 'fastify';
import { generateYupValidationSchema } from './generateYupValidationSchema';
import type { SchemaProperty } from 'types/schema';
import type { BusinessRuleOperation } from 'types/businessRules';
import { createBusinessRulesValidationContext, addBusinessRulesValidation } from './businessRulesYupExtension';
import { processUnitProperties } from './unitConversion';

/**
 * Thrown when a property write is denied because the caller lacks the required
 * SpiceDB permission. Callers should map this to an HTTP 403 with
 * { property, required_permission } in the response body.
 */
export class PropertyAccessError extends Error {
  readonly property: string;
  readonly required_permission: string;

  constructor(property: string, required_permission: string) {
    super(
      `Write access denied: property '${property}' requires '${required_permission}' permission`
    );
    this.name = 'PropertyAccessError';
    this.property = property;
    this.required_permission = required_permission;
  }
}

/**
 * Thrown when property values fail their schema validation (type, required, format, business
 * rule, unit). `messages` holds one `field: reason` line per failure, written for the caller who
 * sent the values: a route answers it as 400 with `details: messages`. Any other error thrown by
 * validation is a server-side fault and propagates as itself.
 */
export class PropertyValidationError extends Error {
  readonly messages: string[];

  constructor(messages: string[]) {
    super(`[filterAndValidateProperties] Validation failed: ${messages.join('; ')}`);
    this.name = 'PropertyValidationError';
    this.messages = messages;
  }
}

/**
 * Context passed to filterAndValidateProperties to enable per-property write
 * access enforcement.
 *
 * The caller is responsible for building checkWritePermission with all
 * SpiceDB context (token, resource type/id, subject, caveat context).
 * It must be fail-closed: it never answers true unless SpiceDB granted it.
 */
export interface PropertyAccessContext {
  /** Current (server-loaded) sphere properties — used for unchanged-bypass diff. */
  currentProperties: Record<string, any>;
  /**
   * Checks whether the calling user has the given SpiceDB permission on the
   * sphere. Returns false on a denial. When SpiceDB cannot answer it either
   * returns false or throws; a thrown error propagates out of
   * filterAndValidateProperties unchanged, so the caller can answer it as
   * "unavailable" (503) rather than as a denial.
   */
  checkWritePermission: (permission: string) => Promise<boolean>;
}

interface SphereProperties {
  [k: string]: any;
}
interface FilterAndValidateResult {
  filteredProperties: SphereProperties;
  yupSchema: Yup.ObjectSchema<any>;
}

/* ────────────────────────────────────────────────────────────────────────── */
/*  MAIN                                                                     */
/* ────────────────────────────────────────────────────────────────────────── */
export async function filterAndValidateProperties(
  properties: SphereProperties,
  schemaProps: SchemaProperty[],
  assignDefaults = false,
  fastify?: FastifyInstance,
  operation?: BusinessRuleOperation,
  fullSchema?: any,
  locale?: string,
  t?: (key: string, params?: Record<string, any>) => string,
  schemaLocalizations?: any,
  /**
   * Allow read-only fields to be set (for internal system operations).
   * Use this when the system needs to set normally read-only fields like timestamps.
   */
  allowReadOnlyFields = false,
  /** When provided, enforces per-property write access controls via SpiceDB. */
  accessContext?: PropertyAccessContext
): Promise<FilterAndValidateResult> {
  fastify?.log.debug(
    { propertyKeys: Object.keys(properties), propCount: schemaProps.length },
    '[filterAndValidateProperties] START validation'
  );

  const nodeEdgeProps = schemaProps;

  fastify?.log.debug(
    '[filterAndValidateProperties] node+edge props',
    nodeEdgeProps.map((p) => p.name)
  );

  /* 2️⃣  Build Yup schema */
  const validationShape = generateYupValidationSchema(nodeEdgeProps);
  let nodeEdgeSchema = Yup.object().shape(validationShape);

  // Add business rules validation if schema and operation provided
  if (fullSchema && operation && locale && t) {
    const businessRulesContext = createBusinessRulesValidationContext(
      operation,
      locale,
      t,
      fastify,
      schemaLocalizations
    );

    nodeEdgeSchema = addBusinessRulesValidation(nodeEdgeSchema, fullSchema, operation, businessRulesContext);

    fastify?.log.debug('[filterAndValidateProperties] Added business rules validation to schema');
  }

  /* 3️⃣  Filter input & apply defaults */
  const filteredProperties: SphereProperties = {};

  fastify?.log.debug(
    { propertyKeys: Object.keys(properties) },
    '[filterAndValidateProperties] filtering properties'
  );

  for (const prop of nodeEdgeProps) {
    let hasValue = Object.hasOwn(properties, prop.name);

    // System-managed read-only: the caller's value is dropped unless explicitly bypassed, and the
    // property is treated as not supplied, so a create still gets its default (an orbit created
    // with a caller-chosen `deploymentStatus` starts at the schema's `draft`, not with no status).
    if (prop.read_only && !prop.immutable && hasValue && !allowReadOnlyFields) {
      fastify?.log.debug(
        `[filterAndValidateProperties] SKIPPING read-only "${prop.name}" (allowReadOnlyFields=${allowReadOnlyFields})`
      );
      hasValue = false;
    }

    if (prop.read_only && !prop.immutable && hasValue && allowReadOnlyFields) {
      fastify?.log.debug(
        `[filterAndValidateProperties] ALLOWING read-only "${prop.name}" (allowReadOnlyFields=${allowReadOnlyFields})`
      );
    }

    // Immutable: allow on create/flow_transition, skip on update (unless bypassed)
    if (prop.immutable && hasValue) {
      if (operation === 'update' && !allowReadOnlyFields) {
        fastify?.log.debug(
          `[filterAndValidateProperties] SKIPPING immutable "${prop.name}" on update (allowReadOnlyFields=${allowReadOnlyFields})`
        );
        continue;
      }
      fastify?.log.debug(
        `[filterAndValidateProperties] ALLOWING immutable "${prop.name}" on ${operation ?? 'unknown'} (allowReadOnlyFields=${allowReadOnlyFields})`
      );
    }

    if (hasValue) {
      filteredProperties[prop.name] = properties[prop.name];
      continue;
    }

    if (assignDefaults && prop.default_value !== undefined && prop.default_value !== null) {
      try {
        const fieldSchema = validationShape[prop.name] as Yup.Schema<any>;
        const casted = fieldSchema.cast(prop.default_value);
        fieldSchema.validateSync(casted);
        filteredProperties[prop.name] = casted;
        fastify?.log.debug(
          `[filterAndValidateProperties] assigned default for "${prop.name}"`,
          casted
        );
      } catch (err) {
        fastify?.log.debug(
          `[filterAndValidateProperties] invalid default for "${prop.name}"`,
          err
        );
      }
    }
  }

  /* 3.4️⃣  Per-property write access enforcement */
  if (accessContext) {
    for (const prop of nodeEdgeProps) {
      if (!prop.access?.write) continue;
      const propName = prop.name;
      if (!(propName in filteredProperties)) continue;

      // Unchanged-bypass: compare new value against server-loaded current.
      // Uses JSON.stringify for deep equality; accepts minor type coercion
      // differences (e.g. boolean vs string 'true') as conservative over-checks.
      const newVal = filteredProperties[propName];
      const currentVal = accessContext.currentProperties[propName];
      if (JSON.stringify(newVal) === JSON.stringify(currentVal)) {
        fastify?.log.debug(
          `[filterAndValidateProperties] Access check bypassed for unchanged "${propName}"`
        );
        continue;
      }

      // fail-closed: a denial is false; a check SpiceDB cannot answer is false or throws (propagated)
      const allowed = await accessContext.checkWritePermission(prop.access.write);
      if (!allowed) {
        fastify?.log.warn(
          { propName, required: prop.access.write },
          `[filterAndValidateProperties] Write access denied for "${propName}"`
        );
        throw new PropertyAccessError(propName, prop.access.write);
      }

      fastify?.log.debug(
        `[filterAndValidateProperties] Write access granted for "${propName}" (requires "${prop.access.write}")`
      );
    }
  }

  /* 3.5️⃣  Process unit-type properties to convert to UnitValue format */
  let processedProperties: SphereProperties;
  try {
    processedProperties = processUnitProperties(filteredProperties, nodeEdgeProps);
  } catch (error: unknown) {
    // A unit the schema does not allow, or a value that is not a unit at all: the caller's input.
    throw new PropertyValidationError([error instanceof Error ? error.message : String(error)]);
  }
  Object.assign(filteredProperties, processedProperties);

  fastify?.log.debug({
    filteredPropertiesKeys: Object.keys(filteredProperties),
    allowReadOnlyFields
  }, '[filterAndValidateProperties] Final filtered node/edge props - ABOUT TO VALIDATE');

  /* 4️⃣  Validate node/edge */
  try {
    await nodeEdgeSchema.validate(filteredProperties, { strict: false, abortEarly: false });
    fastify?.log.debug('[filterAndValidateProperties] node/edge validation OK');
  } catch (error: unknown) {
    // A yup message can quote the rejected value, so the log names fields and rule types only.
    if (!(error instanceof Yup.ValidationError)) {
      // A rule or schema that threw is a server-side fault, not the caller's input: propagate it.
      fastify?.log.error(
        { err: error, propertyKeys: Object.keys(filteredProperties) },
        '[filterAndValidateProperties] Validation could not run'
      );
      throw error;
    }

    const failures = error.inner.length ? error.inner : [error];
    fastify?.log.info(
      { failures: failures.map((e) => ({ path: e.path, type: e.type })) },
      '[filterAndValidateProperties] node/edge validation failed'
    );
    throw new PropertyValidationError(
      failures.map((e) => `${e.path || 'unknown field'}: ${e.message}`)
    );
  }
  return { filteredProperties, yupSchema: nodeEdgeSchema };
}
